import React, { useState, useRef, FC, useEffect, useCallback, useMemo } from 'react';
import SingleSelectDropdown from '../../atoms/single-select-dropdown/single-select-dropdown';
import MultiSelectComponent from '../../atoms/multi-select/multi-select';
import { Sphere } from 'types/global';
import { FilterExpression } from 'types/filters';
import type { GlyphColor } from '../../../types/glyph';
import './SphereSearch.scss';
import { Badge } from 'primereact/badge';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import SphereRowMolecule from '../SphereRow/SphereRowMolecule';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';

// Use the same interface as the design system atoms
export interface SphereOption {
  name: string;
  code: string;
  glyph?: string;
  glyphColor?: GlyphColor;
  imageId?: string;
  sphere?: Sphere; // Make optional to match OptionType interface
}

export interface SphereFilter {
  relationshipType?: string;
  operator?: 'in' | 'not in';
  spheres: string[];
  filters?: Record<string, any>;
}

interface SphereSearchProps {
  relationshipType?: string;        // Filter constraint from relationship schema
  organizationId: string;
  onSphereSelected?: (
    selectedSpheres: SphereOption[],
    selectedSphere?: SphereOption | null  // For single mode convenience
  ) => void;
  filters?: Record<string, any>;    // Additional filters from relationship target_filter
  filterOperator?: 'and' | 'or';   // Logical operator for combining filters (defaults to 'or')
  placeholder?: string;
  className?: string;
  initialSelectedSpheres?: SphereOption[];
  single?: boolean;                 // Single or multi-select mode
  showLabel?: boolean;
  label?: React.ReactNode;
  isEditing?: boolean;
  contextFilters?: FilterExpression[] | null; // Context-aware search filters from active context
}

const SphereSearch: FC<SphereSearchProps> = ({
  relationshipType,
  organizationId,
  onSphereSelected,
  filters = {},
  filterOperator = 'or', // Default to 'or' for backwards compatibility
  placeholder = "Search spheres...",
  className = '',
  initialSelectedSpheres = [],
  single = false,
  showLabel = false,
  label = '',
  isEditing = false,
  contextFilters,
}) => {
  const apiClients = useApiClientsOrNull();
  
  if (!apiClients) {
    return null;
  }
  
  const { graphClient } = apiClients;
  const nodeRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Data states
  const [options, setOptions] = useState<SphereOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSpheres, setSelectedSpheres] = useState<SphereOption[]>(
    initialSelectedSpheres || []
  );
  const MAX_VISIBLE_CHIPS = 5;

  const [showAllChips, setShowAllChips] = useState(false);
  // Track if we've already loaded data to prevent loops
  const [hasLoadedData, setHasLoadedData] = useState(false);
  
  // Memoize filters to prevent unnecessary re-renders
  const memoizedFilters = useMemo(() => filters, [JSON.stringify(filters)]);

  // Memoize contextFilters to prevent unnecessary re-renders
  const memoizedContextFilters = useMemo(() => contextFilters, [JSON.stringify(contextFilters)]);
  
  // Memoize initialSelectedSpheres to prevent unnecessary re-renders
  const memoizedInitialSelectedSpheres = useMemo(() => {
    return initialSelectedSpheres || [];
  }, [JSON.stringify(initialSelectedSpheres?.map(s => ({ code: s.code, name: s.name })) || [])]);

  // Initialize selectedSpheres when memoized version changes
  useEffect(() => {
    setSelectedSpheres(memoizedInitialSelectedSpheres);
  }, [memoizedInitialSelectedSpheres]);

  // Reset data loading state when key parameters change
  useEffect(() => {
    setHasLoadedData(false);
    setOptions([]);
  }, [relationshipType, organizationId, memoizedFilters, memoizedContextFilters]);

  const fetchData = useCallback(async (query = '', pageNum = 1): Promise<void> => {
    
    setIsLoading(true);
    try {
      // Build request body for POST request
      const requestBody: Record<string, any> = {
        searchTerm: query,
        page: pageNum,
        limit: 100,
        filters: [],
        filterOperator: filterOperator // Pass the filter operator to the API
      };

      // Note: relationshipType is used for determining target_filter but should not be sent as a search filter
      // The relationshipType is metadata about the relationship, not a property of target spheres

      // Add additional filters from relationship target_filter
      if (Array.isArray(memoizedFilters)) {
        requestBody.filters.push(...memoizedFilters);
      } else {
        // Convert object filters to array format
        Object.keys(memoizedFilters).forEach(key => {
          const filterValue = memoizedFilters[key];
          if (typeof filterValue === 'object' && filterValue.property && filterValue.value) {
            requestBody.filters.push(filterValue);
          } else {
            requestBody.filters.push({
              property: key,
              value: filterValue
            });
          }
        });
      }

      // Add context-aware search filters
      if (memoizedContextFilters && memoizedContextFilters.length > 0) {
        requestBody.contextFilters = memoizedContextFilters;
      }

      const response = await graphClient.post('/search', requestBody);
      
      // API client already extracts .data, so response IS the data: { spheres: [...], pagination: {...} }
      const spheres = response?.spheres || [];

      const newOptions = spheres.map((sphere: Sphere) => ({
        name: sphere.properties?.name || sphere.id,
        code: sphere.id,
        glyph: sphere.glyph,
        glyphColor: sphere.glyphColor,
        imageId: sphere.properties?.imageId,
        sphere: sphere,
      }));

      // Use functional state updates to avoid dependency issues
      setOptions(prevOptions => {
        // Merge with existing options if paginating
        return pageNum === 1 ? newOptions : [...prevOptions, ...newOptions];
      });

    } catch (error: any) {
      console.error('SphereSearch - API Error:', error);
      setError('Failed to load spheres. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [graphClient, organizationId, relationshipType, memoizedFilters, memoizedContextFilters]);

  // Only load data when dropdown is opened, not automatically
  const handleDropdownShow = useCallback(() => {
    if (relationshipType && organizationId && !hasLoadedData) {
      setError(null);
      setHasLoadedData(true);
      fetchData('', 1);
    }
  }, [relationshipType, organizationId, hasLoadedData, fetchData]);

  // For MultiSelect (which doesn't expose onShow), load data when needed
  useEffect(() => {
    if (!single && relationshipType && organizationId && !hasLoadedData) {
      setError(null);
      setHasLoadedData(true);
      fetchData('', 1);
    }
  }, [single, relationshipType, organizationId, hasLoadedData, fetchData]);

  // Handle search query changes with debouncing to prevent input clearing
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  
  const handleSearch = useCallback((query: string) => {
    setError(null);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce the actual API call to prevent too many requests
    searchTimeoutRef.current = setTimeout(() => {
      // Don't update hasLoadedData for search queries - they're independent of initial load
      fetchData(query, 1);
    }, 300);
  }, [fetchData]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const itemTemplate = (option: SphereOption) => {
    return (
      <SphereRowMolecule
        variant="compact"
        sphereId={option.code}
        sphereName={option.name}
        glyph={option.glyph}
        glyphColor={option.glyphColor}
        imageId={option.imageId}
      />
    );
  };

  // Handle selection changes for single select
  const handleSingleChange = (e: { value: SphereOption | null }) => {
    const newSelection = e.value ? [e.value] : [];
    setSelectedSpheres(newSelection);

    const singleSphere = newSelection.length > 0 ? newSelection[0] : null;
    onSphereSelected?.(newSelection, singleSphere);
  };

  // Handle selection changes for multi select
  const handleMultiChange = (selected: SphereOption[] | null) => {
    const newSelection = selected || [];
    setSelectedSpheres(newSelection);

    const singleSphere = newSelection.length > 0 ? newSelection[0] : null;
    onSphereSelected?.(newSelection, singleSphere);
  };

  // Remove an item from the selected list (chip removal)
  const removeSelection = (code: string) => {
    const newSelectedSpheres = selectedSpheres.filter(
      (sphere) => sphere.code !== code
    );
    setSelectedSpheres(newSelectedSpheres);
    
    const singleSphere = newSelectedSpheres.length > 0 ? newSelectedSpheres[0] : null;
    onSphereSelected?.(newSelectedSpheres, singleSphere);
  };

  const clearSelections = () => {
    setSelectedSpheres([]);
    onSphereSelected?.([], null);
  };

  const toggleShowAll = () => {
    setShowAllChips(!showAllChips);
  };

  useEffect(() => {
    setShowAllChips(false);
  }, [selectedSpheres]);


  return (
    <div
      ref={nodeRef}
      className={`sphere-search-component ${className} ${isEditing ? 'editing' : ''}`}
    >
      <div className="sphere-search-container" ref={dropdownRef}>
        {showLabel && label && (
          <label className="sphere-search-label">{label}</label>
        )}
        
        {single ? (
          <SingleSelectDropdown
            value={selectedSpheres[0] || null}
            options={options}
            onChange={handleSingleChange}
            optionLabel="name"
            placeholder={placeholder}
            className="sphere-search-dropdown"
            itemTemplate={itemTemplate}
            filter
            loading={isLoading}
            onShow={handleDropdownShow}
            onFilter={(e: any) => handleSearch(e.filter)}
          />
        ) : (
          <MultiSelectComponent
            value={selectedSpheres as any} // Type assertion to handle interface mismatch
            options={options as any}
            onSelectionChange={handleMultiChange as any}
            placeholder={placeholder}
            className="sphere-search-multiselect"
            filter
          />
        )}

        {/* Show selected spheres as chips for multi-select */}
        {!single && selectedSpheres.length > 0 && (
          <div className="selected-chips-container">
            {selectedSpheres.length > MAX_VISIBLE_CHIPS && !showAllChips && (
              <Badge
                value={`+${selectedSpheres.length - MAX_VISIBLE_CHIPS}`}
                severity="info"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleShowAll();
                }}
                className="extra-items-badge"
                style={{ cursor: 'pointer', marginLeft: '5px' }}
              />
            )}
            {selectedSpheres
              .slice(0, showAllChips ? selectedSpheres.length : MAX_VISIBLE_CHIPS)
              .map((sphere) => (
                <ChipAtom
                  key={sphere.code}
                  label={sphere.name}
                  removable
                  onRemove={() => {
                    removeSelection(sphere.code);
                    return false;
                  }}
                  className="selected-chip"
                />
              ))}
          </div>
        )}

        {/* Clear All button - only show for multi-select */}
        {!single && selectedSpheres.length > 0 && (
          <ButtonAtom
            className="clear-button"
            onClick={clearSelections}
            variant="secondary"
            size="small"
            style={{
              marginTop: '0.5rem',
              marginRight: '1rem',
              marginBottom: '0.5rem',
            }}
          >
            Clear All
          </ButtonAtom>
        )}

        {error && <span className="error-message">{error}</span>}
      </div>
    </div>
  );
};

export default SphereSearch;